import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import ICatalog from "@models/ICatalog";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import SupplierService from "@services/SupplierService";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import {
    buildOrdersFilterSections,
    ORDERS_DEFAULT_FILTERS,
    PERCENT_FILLABLE_API_KEY,
    QUOTES_DEFAULT_FILTERS,
    TEMPLATES_DEFAULT_FILTERS,
} from "@config/filters/ordersFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";
import { useOrderFilterData } from "./useOrderFilterData";

export const ORDERS_FILTER_ACTIONS_ID = "orders-filter-actions";

/** Query params on the Orders URL that are not filters. */
const NON_FILTER_PARAMS = ["page", "limit", "create", "is_quote", "is_template", "vendor_name", "order_by", "order_dir"];

type Props = {
    catalogs: ICatalog[];
    hasQuotes: boolean;
    hasTemplates: boolean;
    /** Quotes screen (`/quotes`): quote-only filter set, `quotes` presets and `is_quote=1` on every search */
    isQuotes?: boolean;
    /** Templates screen (`/templates`): template filter set, `templates` presets and `is_template=1` on every search */
    isTemplates?: boolean;
    isSearching: boolean;
    /** Existing Orders fetch: receives the complete `?a=b&c=d` query string and the percent-fillable request flag */
    searchFn: (queryString: string, percentageFillable?: boolean) => void;
    /** Lets the parent rebuild the query string when the sort changes */
    currentQueryStringArr: (parts: string[]) => void;
};

/**
 * Orders / Quotes / Templates integration of the shared FilterBar (v2 search UI): compiles the filter sections,
 * loads/persists presets, syncs the URL, and hands the query string to the existing fetch.
 */
export const OrdersFilterBar = (props: Props) => {
    const { catalogs, hasQuotes, hasTemplates, searchFn, currentQueryStringArr } = props;
    const isQuotes = !!props.isQuotes;
    const isTemplates = !isQuotes && !!props.isTemplates;
    const screenKey = isQuotes ? ScreenOptionsEnum.QUOTES : isTemplates ? ScreenOptionsEnum.TEMPLATES : ScreenOptionsEnum.ORDERS;
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();
    const navigate = useNavigate();

    const formatMessage = useCallback(
        (id: string) => (intl.messages && intl.messages[id] !== undefined ? intl.formatMessage({ id }) : id),
        [intl]
    );

    // ── 1. Compile filter sections with dynamic data ──
    const data = useOrderFilterData();

    const sections = useMemo(
        () =>
            buildOrdersFilterSections({
                supplierModel,
                userModel,
                catalogs,
                users: data.users,
                orderCategories: data.orderCategories,
                orderRoutes: data.orderRoutes,
                workflows: data.workflows,
                salesReps: data.salesReps,
                orderStatuses: data.orderStatuses,
                lookups: data.lookups,
                formatMessage,
                isQuotes,
                isTemplates,
            }),
        [
            supplierModel,
            userModel,
            catalogs,
            data.users,
            data.orderCategories,
            data.orderRoutes,
            data.workflows,
            data.salesReps,
            data.orderStatuses,
            data.lookups,
            formatMessage,
            isQuotes,
            isTemplates,
        ]
    );

    // Deep links (e.g. /orders?order_state=2&filter_shipped=true, /quotes?filter_closed=true): read on mount
    // and again on Back / Forward. Pagination-only URL changes (page / limit) do not count as a filter change.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, NON_FILTER_PARAMS);

    // ── 2. Presets: migrate legacy orders_filters / quotes_filters, merge team + user, persist on change ──
    const { presets, onPresetsChange } = useFilterPresets(screenKey);

    // ── 3. Search: query object → queryString transport ──
    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            const { [PERCENT_FILLABLE_API_KEY]: percentFillable, ...rest } = query;
            const filterParts = toQueryStringParts(rest);

            // Keep the URL in sync so filtered views can be shared / deep-linked (as the legacy filter does).
            // The mount search replaces (no extra history entry); user searches push.
            // Percent Fillable stays in the URL (not in the API queryString) so a refresh restores it and
            // still matches the preset it came from.
            const urlParts = toQueryStringParts(query);
            const pathname = isQuotes
                ? "/quotes"
                : isTemplates
                  ? "/templates"
                  : SupplierService.updateStringWithOrderNameOverride("/orders", supplierModel);

            navigate({ pathname, search: urlParts.length ? `?${urlParts.join("&")}` : "" }, { replace: isFirstSearch.current });
            isFirstSearch.current = false;

            const parts = [...filterParts];

            // Same screen flags the legacy OrderFilter.performSearch appends.
            if (isQuotes) {
                parts.push("is_quote=1");
            } else if (hasQuotes) {
                parts.push("is_quote=0");
            }

            if (isTemplates) {
                parts.push("is_template=1");
            } else if (hasTemplates) {
                parts.push("is_template=0");
            }

            searchFn(parts.length ? `?${parts.join("&")}` : "", percentFillable === true ? true : undefined);
            currentQueryStringArr(parts);
        },
        [isQuotes, isTemplates, supplierModel, navigate, hasQuotes, hasTemplates, searchFn, currentQueryStringArr, isFirstSearch]
    );

    // ── 4. Feedback: POST /user/feedback with the screen context FilterBar prepends ──
    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    const allLabel = useMemo(
        () =>
            isQuotes
                ? "all_quotes"
                : isTemplates
                  ? "all_templates"
                  : SupplierService.updateStringWithOrderNameOverride("all_orders", supplierModel),
        [isQuotes, isTemplates, supplierModel]
    );

    const defaultFilters = isQuotes ? QUOTES_DEFAULT_FILTERS : isTemplates ? TEMPLATES_DEFAULT_FILTERS : ORDERS_DEFAULT_FILTERS;

    // Wait for the first merge so a default preset can auto-load on mount.
    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={screenKey}
            mode={"drawer"}
            defaultFilters={defaultFilters}
            initialFilters={initialFilters}
            allLabel={allLabel}
            actionsContainerId={ORDERS_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default OrdersFilterBar;
